import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Row, Col, Select, Switch, Typography } from 'antd';

import { constants } from 'helpers/constants';
import { EChargeType } from 'interfaces/IEditProduct';
import { EPartnerCode } from 'interfaces/IProduct';
import type IProduct from 'interfaces/IProduct';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { useUserStore } from 'v2/store/User';

import { FormContent } from '../FormContent';
import BlockedFieldTooltipProps from './../General/blockedTooltipProps';
import { Offers } from './components/Offers';
import { TaxAlert } from './components/Offers/TaxAlert';
import SinglePayment from './components/SinglePayment';
import Subscription from './components/Subscription';

interface IProps {
  loading: boolean;
  fade?: any;
  product?: IProduct;
  modifieds?: any[];
  editing?: any;
  handleChange?: any;
  copyFromOriginal?: any;
  onChangeRequiredFields?: any;
  openConfirmModal: () => void;
}

function Sale(props: IProps) {
  const { loading, handleChange, product, modifieds, editing, openConfirmModal, onChangeRequiredFields } = props;

  const { user } = useUserStore(({ user }) => ({ user }));

  const [validationErrors, setValidationErrors] = useState<any>({});
  const [singlePaymentErrors, setSinglePaymentErrors] = useState<any>({});
  const [subscriptionErrors, setSubscriptionErrors] = useState<any>({});

  const prevProductRef = useRef(product);
  const hasRunOnMountRef = useRef(false);

  const onChangeRequired = useCallback((name: string, fields: any) => {
    if (name === 'singlePaymentErrors') {
      setSinglePaymentErrors(fields);
    }

    if (name === 'subscriptionErrors') {
      setSubscriptionErrors(fields);
    }
  }, []);

  const onChangeStates = useCallback(() => {
    if (!product) {
      return;
    }

    let newValidations: any = validationErrors;

    if (product.charge_type === EChargeType.SINGLE) {
      newValidations = { ...singlePaymentErrors };
    }

    if (product.charge_type === EChargeType.SUBSCRIPTION) {
      newValidations = { ...subscriptionErrors };
    }

    setValidationErrors(newValidations);

    if (onChangeRequiredFields) {
      onChangeRequiredFields('salesRequiredFields', newValidations ? Object.keys(newValidations).length : 0);
    }
  }, [product, singlePaymentErrors, subscriptionErrors, validationErrors, onChangeRequiredFields]);

  const disableOpenPriceBlockedFields = useCallback(() => {
    if (!handleChange || !product) return;
    if (product.trial) handleChange('trial', false);
    if (product.membership_fee) handleChange('membership_fee', false);
    if (product.allows_upgrade) handleChange('allows_upgrade', false);
    if (product.allows_downgrade) handleChange('allows_downgrade', false);
  }, [handleChange, product]);

  const handleChangeOpenPrice = useCallback(
    (openPrice: boolean) => {
      if (!handleChange) return;
      handleChange('open_price', openPrice);

      if (openPrice) {
        disableOpenPriceBlockedFields();
      }
    },
    [handleChange, disableOpenPriceBlockedFields]
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    const productChanged = product !== prevProductRef.current;

    if (!hasRunOnMountRef.current) {
      hasRunOnMountRef.current = true;
      onChangeStates();
    } else {
      if (productChanged) {
        onChangeStates();
      }
    }

    if (productChanged && product && handleChange) {
      if (product.charge_type !== EChargeType.SUBSCRIPTION && product.open_price) {
        handleChange('open_price', false);
      }

      if (product.charge_type === EChargeType.SUBSCRIPTION && product.open_price) {
        disableOpenPriceBlockedFields();
      }
    }

    prevProductRef.current = product;
  }, [loading, product, onChangeStates, handleChange, disableOpenPriceBlockedFields]);

  if (!product) {
    return null;
  }

  const userHasAccessToOpenPriceSubscriptionModule =
    eduzzAccountsService.verifyUserHasBetaModule('valor_aberto_assinaturas');
  const isAlumy = product.cnt_partner_cod === EPartnerCode.ALUMY;
  const isBlinket = product.cnt_partner_cod === EPartnerCode.BLINKET;
  const isSinglePayment = product.charge_type === EChargeType.SINGLE;
  const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;
  const isInfo = product.type === constants.INFO;
  const hasOffersAccess = isSinglePayment && isInfo;
  const isCreatingNewVariation = product.isVariation() && !product.id;
  const showCurrencyLabel = (isCreatingNewVariation || !isInfo) && (isSinglePayment || isSubscription);
  const hasUnsavedChanges = Object.keys(modifieds || {}).length > 0;
  const allowRecurrency =
    isInfo || product.type === constants.SERVICE || (product.type === constants.PHYSICAL && user.subscription_physical);

  return (
    <Row gutter={[16, 16]} align='middle'>
      <Col span={24}>
        <FormContent title='Formas de cobrança' loading={loading}>
          <Row gutter={[16, 16]} align='middle'>
            <Col span={24}>
              <BlockedFieldTooltipProps
                partnerCod={product.cnt_partner_cod}
                enabled={isAlumy || isBlinket}
                title={isAlumy ? 'Produto do tipo Alumy não pode ser alterado o tipo de cobrança' : null}
              >
                <div>
                  <Typography.Text className='mb-1 block text-base'>Tipo de cobrança</Typography.Text>
                  <Select
                    id='charge_type'
                    className='w-full'
                    value={product.charge_type}
                    onChange={value => {
                      if (value === EChargeType.OTHER) {
                        handleChange('recovery_email', '');
                        handleChange('sales_recovery', false);
                        handleChange('recruit_commission_type', 'P');
                        handleChange('comission_type', 'P');
                      }

                      if (value !== EChargeType.SUBSCRIPTION) {
                        handleChange('trial', false);
                      }

                      if (value !== EChargeType.SUBSCRIPTION && value !== EChargeType.FREE && product.allows_card) {
                        handleChange('allows_multiple_cards', true);
                      }

                      if (value !== EChargeType.SINGLE) {
                        handleChange('interest_free_installment', false);
                      }

                      if (value === 'A') {
                        handleChange('recurring_frequency', 1);
                      }

                      if (value !== EChargeType.SUBSCRIPTION && product.open_price) {
                        handleChange('open_price', false);
                      }

                      handleChange('charge_type', value);
                    }}
                    disabled={isAlumy || isBlinket}
                    options={[
                      { value: EChargeType.SINGLE, label: 'Única' },
                      ...(allowRecurrency
                        ? [{ value: EChargeType.SUBSCRIPTION, label: 'Assinatura/Recorrência' }]
                        : []),
                      ...(product.type === constants.INFO || product.type === constants.SERVICE
                        ? [{ value: EChargeType.OTHER, label: 'Valor Aberto' }]
                        : []),
                      ...(product.type !== 3 ? [{ value: EChargeType.FREE, label: 'Gratuito' }] : [])
                    ]}
                  />
                </div>
              </BlockedFieldTooltipProps>
            </Col>
            {isSubscription && userHasAccessToOpenPriceSubscriptionModule && (
              <Col span={24}>
                <div className='flex items-center gap-2'>
                  <Switch checked={product.open_price} onChange={handleChangeOpenPrice} />
                  <Typography.Text className='text-base'>Valor Aberto</Typography.Text>
                </div>
                <Typography.Paragraph className='text-content-subtitle mt-1 text-base'>
                  Valor mínimo a partir de R$ 5,00
                </Typography.Paragraph>
              </Col>
            )}

            {showCurrencyLabel && (
              <Col xs={24} sm={12} className='self-baseline'>
                <div className='flex flex-col gap-4'>
                  <Typography.Text className='block text-base'>Moeda</Typography.Text>
                  <Typography.Text className='text-base'>BRL (Real Brasileiro)</Typography.Text>
                </div>
              </Col>
            )}

            {product.charge_type === EChargeType.SINGLE && (
              <SinglePayment
                product={product}
                handleChange={handleChange}
                onChangeRequiredFields={onChangeRequired}
                loading={loading}
              />
            )}
            {product.charge_type === EChargeType.SUBSCRIPTION && (
              <Subscription
                product={product}
                handleChange={handleChange}
                onChangeRequiredFields={onChangeRequired}
                modifieds={modifieds}
                editing={editing}
                loading={loading}
                isAlumy={isAlumy}
              />
            )}
            {product.charge_type === EChargeType.OTHER && (
              <Col span={24}>
                <Typography.Paragraph>
                  O valor <strong>minímo</strong> no checkout será de R$ 5,00
                </Typography.Paragraph>
                <Typography.Paragraph>Não será habilitado o parcelamento no checkout</Typography.Paragraph>
              </Col>
            )}
            {product.charge_type === EChargeType.FREE && (
              <>
                <Col xs={24} md={4}>
                  <div>
                    <img src='/img/eduzzinho-thumbsup.png' alt='Eduzzinho' width='100px' className='mr-12.5' />
                  </div>
                </Col>
                <Col xs={24} md={20}>
                  <b>Eba! Produto gratuito.</b>
                  <Typography.Paragraph>
                    Produto gratuito é uma ótima maneira de captar leads, você pode utilizar como produto de entrada
                    para outro produto pago.
                  </Typography.Paragraph>
                </Col>
              </>
            )}
          </Row>
        </FormContent>
      </Col>
      {hasOffersAccess && !isCreatingNewVariation && (
        <>
          <Col span={24}>
            <Offers
              product={product}
              loading={loading}
              hasUnsavedChanges={hasUnsavedChanges}
              openConfirmModal={openConfirmModal}
            />
          </Col>
          <Col span={24}>
            <TaxAlert />
          </Col>
        </>
      )}
    </Row>
  );
}

export default Sale;
